import { motion, useScroll, useTransform } from 'motion/react';
import type { RefObject } from 'react';
import { useRef } from 'react';

/* Target dimensions the hero image scales down to (px) — desktop */
const TARGET_W = 413;
const TARGET_H = 230;

/* Mobile: scale hero to a fraction of its initial size */
const MOBILE_TARGET_RATIO_X = 0.45;
const MOBILE_TARGET_RATIO_Y = 0.30;

/* Spacing constants — desktop / mobile */
const GAP = 48;
const MOBILE_GAP = 130;
const TEXT_GAP = 180;
const MOBILE_TEXT_GAP = 290;
const TEXT_BLOCK_HEIGHT = 220;
const MOBILE_TEXT_BLOCK_HEIGHT = 100;
const BOTTOM_IMG_GAP = 140;
const MOBILE_BOTTOM_IMG_GAP = 80;

/**
 * Encapsulates every scroll-driven motion value so the component
 * body stays under the lint line-count limit.
 */
function useGalleryMotion(
  sectionRef: RefObject<HTMLElement | null>,
  containerRef: RefObject<HTMLDivElement | null>,
  heroRef: RefObject<HTMLDivElement | null>,
) {
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end end'],
  });

  /** Matches Tailwind `md:` breakpoint against viewport. */
  const mobile = () => (containerRef.current?.offsetWidth ?? 1024) < 768;

  /**
   * On mobile the hero shrinks much faster via a steep ease-out.
   * Reaches ~95 % of travel by ~40 % scroll, fully settled by 60 %.
   * The remaining scroll is dead zone where the section unsticks.
   */
  const accel = (p: number) => (mobile() ? 1 - Math.pow(1 - p, 4) : p);

  /** Resolve responsive target height for hero. */
  const getTargetH = (h: number, m: boolean) =>
    m ? h * MOBILE_TARGET_RATIO_Y : TARGET_H;

  /* ── Hero scale ────────────────────────────────── */
  const scaleX = useTransform(scrollYProgress, (p) => {
    const hero = heroRef.current;
    if (!hero) return 1;
    const w = hero.offsetWidth;
    const ap = accel(p);
    const targetW = mobile() ? w * MOBILE_TARGET_RATIO_X : TARGET_W;
    return 1 - ap * (1 - targetW / w);
  });

  const scaleY = useTransform(scrollYProgress, (p) => {
    const hero = heroRef.current;
    if (!hero) return 1;
    const h = hero.offsetHeight;
    const ap = accel(p);
    return 1 - ap * (1 - getTargetH(h, mobile()) / h);
  });

  /**
   * Push offset: at p=0 elements start below the visible area.
   * As scroll progresses the offset shrinks to 0 and elements
   * rise into view. Desktop uses a viewport-height push to
   * guarantee elements start fully clipped by overflow-hidden.
   */
  const staggeredOffset = (ap: number, delay: number) => {
    const local = Math.max(0, (ap - delay) / (1 - delay));
    const push = mobile() ? 200 : (containerRef.current?.offsetHeight ?? 800);
    return push * (1 - local);
  };

  /* ── Right-top image position ──────────────────── */
  const rightImageTop = useTransform(scrollYProgress, (p) => {
    const hero = heroRef.current;
    if (!hero) return 0;
    const h = hero.offsetHeight;
    const m = mobile();
    const ap = accel(p);
    const currentScaleY = 1 - ap * (1 - getTargetH(h, m) / h);
    return hero.offsetTop + currentScaleY * h + (m ? MOBILE_GAP : GAP) + staggeredOffset(ap, 0);
  });

  /* Parallax: right-top image travels further upward */
  const rightImageParallaxY = useTransform(scrollYProgress, (p) => {
    const travel = mobile() ? -140 : -200;
    if (p < 0.45) return 0;
    return ((p - 0.45) / 0.55) * travel;
  });

  /* ── Opacity: staggered fade-in for each element ── */
  const rightImageOpacity = useTransform(scrollYProgress, [0.05, 0.2], [0, 1]);
  const textOpacity = useTransform(scrollYProgress, [0.12, 0.3], [0, 1]);
  const bottomImageOpacity = useTransform(scrollYProgress, [0.2, 0.4], [0, 1]);

  /* ── Text position ─────────────────────────────── */
  const textTop = useTransform(scrollYProgress, (p) => {
    const hero = heroRef.current;
    if (!hero) return 0;
    const h = hero.offsetHeight;
    const m = mobile();
    const ap = accel(p);
    const currentScaleY = 1 - ap * (1 - getTargetH(h, m) / h);
    return hero.offsetTop + currentScaleY * h + (m ? MOBILE_TEXT_GAP : TEXT_GAP) + staggeredOffset(ap, 0.15);
  });

  /* Subtle text parallax — drifts upward as you scroll */
  const textParallaxY = useTransform(scrollYProgress, (p) => {
    const travel = mobile() ? -100 : -40;
    if (p < 0.3) return 0;
    return ((p - 0.3) / 0.7) * travel;
  });

  /* ── Bottom image position ─────────────────────── */
  const bottomImageTop = useTransform(scrollYProgress, (p) => {
    const hero = heroRef.current;
    if (!hero) return 0;
    const h = hero.offsetHeight;
    const m = mobile();
    const ap = accel(p);
    const currentScaleY = 1 - ap * (1 - getTargetH(h, m) / h);
    const tGap = m ? MOBILE_TEXT_GAP : TEXT_GAP;
    const tBlock = m ? MOBILE_TEXT_BLOCK_HEIGHT : TEXT_BLOCK_HEIGHT;
    const bGap = m ? MOBILE_BOTTOM_IMG_GAP : BOTTOM_IMG_GAP;
    return hero.offsetTop + currentScaleY * h + tGap + tBlock + bGap + staggeredOffset(ap, 0.3);
  });

  return {
    scaleX,
    scaleY,
    rightImageTop,
    rightImageParallaxY,
    rightImageOpacity,
    textTop,
    textParallaxY,
    textOpacity,
    bottomImageTop,
    bottomImageOpacity,
  };
}

/**
 * Scroll-driven gallery section.
 *
 * 1. Large hero image pins top-left, scales down to TARGET_W × TARGET_H.
 * 2. Right-top image enters from below as space opens up on the right.
 * 3. Centered text rises into view but stays below images.
 *
 * All motion uses GPU-composited transforms (scale, translateY) — zero CLS.
 */
export const TacticalDronesGallerySection = ({
  largeImage,
  rightTopImage,
  rightBottomImage,
  text,
}: {
  largeImage: string;
  rightTopImage: string;
  rightBottomImage: string;
  text: {
    line1: string;
    line2: string;
  };
}) => {
  const sectionRef = useRef<HTMLElement>(null);
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const heroRef = useRef<HTMLDivElement>(null);

  const {
    scaleX,
    scaleY,
    rightImageTop,
    rightImageParallaxY,
    rightImageOpacity,
    textTop,
    textParallaxY,
    textOpacity,
    bottomImageTop,
    bottomImageOpacity,
  } = useGalleryMotion(sectionRef, imageContainerRef, heroRef);

  return (
    <section
      ref={sectionRef}
      className="relative w-full min-h-[160vh] md:min-h-[calc(100vh+1200px)] md:pb-[80px]"
    >
      {/* Sticky viewport — everything pins here */}
      <div
        ref={imageContainerRef}
        className="sticky z-10 top-[52px] md:min-h-[calc(1080px)] md:top-[85px] left-0 w-full h-screen"
      >
        {/* Hero image — full-width but height-constrained on mobile */}
        <motion.div
          ref={heroRef}
          className="absolute inset-x-0 top-0 h-[38vh] md:inset-0 md:h-auto origin-top-left overflow-hidden"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.1, ease: [0.4, 0, 0.2, 1] }}
          style={{ scaleX, scaleY }}
        >
          <img
            className="w-full h-full object-cover"
            src={largeImage}
            alt="Tactical drone gallery"
          />
        </motion.div>

        {/* Right-top image — parallax: scrolls faster than text */}
        <motion.div
          className="absolute right-0 w-[48%] md:w-[29.2%] aspect-[491/275] overflow-hidden"
          style={{
            top: rightImageTop,
            y: rightImageParallaxY,
            opacity: rightImageOpacity,
          }}
        >
          <img
            className="w-full h-full object-cover"
            src={rightTopImage}
            alt="Tactical drone top view"
          />
        </motion.div>

        {/* Centered text — 80px below hero's current visual bottom */}
        <motion.div
          className="absolute left-1/2 w-full px-4 md:px-[120px]"
          style={{
            top: textTop,
            x: '-50%',
            y: textParallaxY,
            opacity: textOpacity,
          }}
        >
          <p
            className="text-center text-white leading-[1.2] mx-auto"
            style={{
              fontFamily: "'Manrope', sans-serif",
              fontSize: 'clamp(36px, 5vw, 64px)',
              maxWidth: '628px',
            }}
          >
            {text.line1}
            <br />
            {text.line2}
          </p>
        </motion.div>

        {/* Bottom-right image — fades in below text, no Y movement */}
        <motion.div
          className="absolute w-[48%] md:w-[29.2%] aspect-[491/275] overflow-hidden left-0 md:left-auto right-auto md:right-[20%]"
          style={{
            top: bottomImageTop,
            opacity: bottomImageOpacity,
          }}
        >
          <img
            className="w-full h-full object-cover"
            src={rightBottomImage}
            alt="Tactical drone bottom view"
          />
        </motion.div>
      </div>
    </section>
  );
};
